import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import AppShell from "../components/layout/AppShell";
import Card from "../components/common/Card";
import Button from "../components/common/Button";
import FileUploadZone from "../components/common/FileUploadZone";
import DataTable from "../components/common/DataTable";
import EmptyState from "../components/common/EmptyState";
import Modal from "../components/common/Modal";
import PatientInsuranceFormSections from "../components/patient/PatientInsuranceFormSections";
import PatientInsuranceReadOnly from "../components/patient/PatientInsuranceReadOnly";
import { mergePatientWithFormDefaults } from "../constants/patientFormDefaults";
import { getPatient, updatePatient } from "../services/patientService";
import { deleteDocument, getDocumentUrl, listPatientDocuments, uploadPdfs } from "../services/documentService";
import { ACTIONS } from "../services/auditService";
import { formatDateTime, bytesToSize } from "../utils/format";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import { OTHER_SYMPTOM_OPTION, PATIENT_SYMPTOM_OPTIONS } from "../constants/patientSymptoms";
import {
  EMAIL_FORMAT_ERROR,
  PHONE_FORMAT_ERROR,
  isValidEmail,
  isValidPhone,
} from "../utils/validation";

export default function PatientDetailPage() {
  const { id } = useParams();
  const { user, profile } = useAuth();
  const { push } = useToast();
  const isAdmin = profile?.role === "admin";
  const [patient, setPatient] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [uploadProgressByFile, setUploadProgressByFile] = useState({});
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [deleteDocTarget, setDeleteDocTarget] = useState(null);
  const [previewingDocId, setPreviewingDocId] = useState("");
  const [downloadingDocId, setDownloadingDocId] = useState("");
  const [editContactTouched, setEditContactTouched] = useState({ phone: false, email: false });
  const [editSubmitAttempted, setEditSubmitAttempted] = useState(false);
  const formSymptoms = Array.isArray(form?.symptoms) ? form.symptoms : [];
  const hasOtherSymptom = formSymptoms.includes(OTHER_SYMPTOM_OPTION);

  const editContactErrors = useMemo(() => {
    if (!form) return { phone: "", email: "" };
    const show = (key) => editContactTouched[key] || editSubmitAttempted;
    const phoneVal = form.phone ?? "";
    const emailVal = form.email ?? "";
    return {
      phone: show("phone") && phoneVal.trim() && !isValidPhone(phoneVal) ? PHONE_FORMAT_ERROR : "",
      email: show("email") && emailVal.trim() && !isValidEmail(emailVal) ? EMAIL_FORMAT_ERROR : "",
    };
  }, [form, editContactTouched, editSubmitAttempted]);

  const editContactInvalid =
    form &&
    ((String(form.phone ?? "").trim() !== "" && !isValidPhone(form.phone ?? "")) ||
      (String(form.email ?? "").trim() !== "" && !isValidEmail(form.email ?? "")));

  const actor = { uid: user?.uid, email: user?.email, displayName: profile?.displayName };

  const load = useCallback(async () => {
    const [p, docs] = await Promise.all([getPatient(id), listPatientDocuments(id)]);
    setPatient(p);
    setForm(mergePatientWithFormDefaults(p));
    setDocuments(docs.sort((a, b) => (b.uploadedAt?.seconds || 0) - (a.uploadedAt?.seconds || 0)));
  }, [id]);

  const onSymptomToggle = (symptom, checked) => {
    setForm((prev) => {
      if (!prev) return prev;
      const current = Array.isArray(prev.symptoms) ? prev.symptoms : [];
      const symptoms = checked ? [...new Set([...current, symptom])] : current.filter((item) => item !== symptom);
      return {
        ...prev,
        symptoms,
        otherSymptomDetails:
          symptom === OTHER_SYMPTOM_OPTION && !checked ? "" : prev.otherSymptomDetails || "",
      };
    });
  };

  useEffect(() => {
    if (id) load();
  }, [id, load]);

  useEffect(() => {
    if (editing) {
      setEditContactTouched({ phone: false, email: false });
      setEditSubmitAttempted(false);
    }
  }, [editing]);

  const startUpload = async (files) => {
    if (!files?.length) return;
    const valid = [];
    for (const file of files) {
      if (file.type !== "application/pdf") {
        push(`Skipped ${file.name}: only PDF files are allowed`, "error");
        continue;
      }
      if (file.size > 20 * 1024 * 1024) {
        push(`Skipped ${file.name}: file must be under 20MB`, "error");
        continue;
      }
      valid.push(file);
    }
    if (!valid.length) return;

    setUploading(true);
    await uploadPdfs(id, valid, actor, (fileName, progress) => {
      setUploadProgressByFile((prev) => ({ ...prev, [fileName]: progress }));
    });
    setUploading(false);
    setUploadProgressByFile({});
    push(`Upload complete (${valid.length} file${valid.length > 1 ? "s" : ""})`, "success");
    load();
  };

  const getDocActionErrorMessage = (err, fallback) => {
    if (err?.code === "storage/unauthorized") return "Only admin users can access this file.";
    if (err?.code === "storage/object-not-found") return "This file no longer exists in storage.";
    if (err?.code === "document/missing-storage-path") return "This document is missing its file path metadata.";
    return fallback;
  };

  const handlePreview = async (docRow) => {
    if (!isAdmin) {
      push("Only admin users can preview documents.", "error");
      return;
    }
    const docId = docRow.docId || docRow.id;
    setPreviewingDocId(docId);
    try {
      const url = await getDocumentUrl(docRow.storagePath, actor, docId, ACTIONS.VIEW_PDF, docRow.fileName);
      const previewTab = window.open(url, "_blank", "noopener,noreferrer");
      if (!previewTab) {
        setPreviewUrl(url);
      }
    } catch (err) {
      push(getDocActionErrorMessage(err, "Unable to preview this document right now."), "error", {
        actionLabel: "Retry",
        onAction: () => handlePreview(docRow),
        duration: 6000,
      });
    } finally {
      setPreviewingDocId("");
    }
  };

  const handleDownload = async (docRow) => {
    if (!isAdmin) {
      push("Only admin users can download documents.", "error");
      return;
    }
    const docId = docRow.docId || docRow.id;
    setDownloadingDocId(docId);
    try {
      const url = await getDocumentUrl(docRow.storagePath, actor, docId, ACTIONS.DOWNLOAD_PDF, docRow.fileName);
      const link = document.createElement("a");
      link.href = url;
      link.download = docRow.fileName || "document.pdf";
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      push(getDocActionErrorMessage(err, "Unable to download this document right now."), "error", {
        actionLabel: "Retry",
        onAction: () => handleDownload(docRow),
        duration: 6000,
      });
    } finally {
      setDownloadingDocId("");
    }
  };

  const columns = [
    { key: "fileName", label: "File Name" },
    { key: "uploaderName", label: "Uploaded By" },
    { key: "uploadedAt", label: "Upload Date", render: (d) => formatDateTime(d.uploadedAt) },
    { key: "fileSize", label: "Size", render: (d) => bytesToSize(d.fileSize) },
  ];

  if (isAdmin) {
    columns.push(
      {
        key: "preview",
        label: "Preview",
        render: (d) => (
          <button
            type="button"
            className="btn btn-secondary"
            disabled={previewingDocId === (d.docId || d.id)}
            onClick={() => handlePreview(d)}
          >
            {previewingDocId === (d.docId || d.id) ? "Opening..." : "Preview"}
          </button>
        ),
      },
      {
        key: "download",
        label: "Download",
        render: (d) => (
          <button
            type="button"
            className="btn btn-secondary"
            disabled={downloadingDocId === (d.docId || d.id)}
            onClick={() => handleDownload(d)}
          >
            {downloadingDocId === (d.docId || d.id) ? "Starting..." : "Download"}
          </button>
        ),
      },
      {
        key: "delete",
        label: "Delete",
        render: (d) => (
          <button
            type="button"
            className="btn btn-danger"
            onClick={() => setDeleteDocTarget(d)}
          >
            Delete
          </button>
        ),
      }
    );
  }

  return (
    <AppShell title="Patient Detail">
      {!patient ? (
        <Card>Loading patient...</Card>
      ) : (
        <div className="two-col">
          <Card id="documents">
            <div className="row-between">
              <h2 className="h2">Patient Info</h2>
              {!editing ? (
                <Button variant="secondary" onClick={() => setEditing(true)}>
                  Edit Patient
                </Button>
              ) : null}
            </div>
            {!editing ? (
              <div style={{ marginTop: 12, fontSize: 14 }}>
                <p>
                  <strong>Name:</strong> {patient.firstName} {patient.lastName}
                </p>
                <p>
                  <strong>DOB:</strong> {patient.dateOfBirth}
                </p>
                <p>
                  <strong>Phone:</strong> {patient.phone || "-"}
                </p>
                <p>
                  <strong>Email:</strong> {patient.email || "-"}
                </p>
                <p>
                  <strong>MRN:</strong> {patient.mrn || "-"}
                </p>
                <p>
                  <strong>Symptoms:</strong>{" "}
                  {Array.isArray(patient.symptoms) && patient.symptoms.length ? patient.symptoms.join(", ") : "-"}
                </p>
                {Array.isArray(patient.symptoms) && patient.symptoms.includes(OTHER_SYMPTOM_OPTION) ? (
                  <p>
                    <strong>Other Symptom Details:</strong> {patient.otherSymptomDetails || "-"}
                  </p>
                ) : null}
                <p>
                  <strong>Notes:</strong> {patient.notes || "-"}
                </p>
                <PatientInsuranceReadOnly patient={patient} />
                <p className="muted">
                  Created by {patient.createdByName} · {formatDateTime(patient.createdAt)}
                </p>
                <p className="muted">
                  Updated by {patient.updatedByName} · {formatDateTime(patient.updatedAt)}
                </p>
              </div>
            ) : (
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  setEditSubmitAttempted(true);
                  if (editContactInvalid) return;
                  await updatePatient(patient.id, form, actor);
                  setEditing(false);
                  push("Patient updated", "success");
                  load();
                }}
              >
                <div className="form-row">
                  <label>First Name</label>
                  <input value={form.firstName} onChange={(e) => setForm((p) => ({ ...p, firstName: e.target.value }))} />
                </div>
                <div className="form-row">
                  <label>Last Name</label>
                  <input value={form.lastName} onChange={(e) => setForm((p) => ({ ...p, lastName: e.target.value }))} />
                </div>
                <div className="form-row">
                  <label>Date Of Birth</label>
                  <input type="date" value={form.dateOfBirth} onChange={(e) => setForm((p) => ({ ...p, dateOfBirth: e.target.value }))} />
                </div>
                <div className="form-row">
                  <label>Phone</label>
                  <input
                    value={form.phone || ""}
                    onBlur={() => setEditContactTouched((p) => ({ ...p, phone: true }))}
                    onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                  />
                  {editContactErrors.phone ? <div className="error-text">{editContactErrors.phone}</div> : null}
                </div>
                <div className="form-row">
                  <label>Email</label>
                  <input
                    type="email"
                    value={form.email || ""}
                    onBlur={() => setEditContactTouched((p) => ({ ...p, email: true }))}
                    onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                  />
                  {editContactErrors.email ? <div className="error-text">{editContactErrors.email}</div> : null}
                </div>
                <div className="form-row">
                  <label>Medical Record Number</label>
                  <input value={form.mrn || ""} onChange={(e) => setForm((p) => ({ ...p, mrn: e.target.value }))} />
                </div>
                <div className="form-row">
                  <label>Symptoms (check all that apply)</label>
                  <div className="symptom-checklist">
                    {PATIENT_SYMPTOM_OPTIONS.map((symptom) => (
                      <label key={symptom} className="symptom-option">
                        <input
                          type="checkbox"
                          className="symptom-checkbox"
                          checked={formSymptoms.includes(symptom)}
                          onChange={(e) => onSymptomToggle(symptom, e.target.checked)}
                        />
                        <span>{symptom}</span>
                      </label>
                    ))}
                  </div>
                  {hasOtherSymptom ? (
                    <div style={{ marginTop: 10 }}>
                      <label>Other Details</label>
                      <input
                        placeholder="Enter additional symptom details"
                        value={form.otherSymptomDetails || ""}
                        onChange={(e) => setForm((p) => ({ ...p, otherSymptomDetails: e.target.value }))}
                      />
                    </div>
                  ) : null}
                </div>
                <div className="form-row">
                  <label>Notes</label>
                  <textarea rows={4} value={form.notes || ""} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} />
                </div>
                <PatientInsuranceFormSections form={form} setForm={setForm} />
                <div className="inline-actions" style={{ justifyContent: "flex-end" }}>
                  <Button variant="secondary" type="button" onClick={() => setEditing(false)}>
                    Cancel
                  </Button>
                  <Button type="submit">Save</Button>
                </div>
              </form>
            )}
          </Card>
          <Card>
            <div className="row-between">
              <h2 className="h2">Documents</h2>
              <span className="badge badge-pending">{documents.length}</span>
            </div>
            <div style={{ marginTop: 12 }}>
              <FileUploadZone onFiles={startUpload} uploading={uploading} progressByFile={uploadProgressByFile} />
              {uploading ? (
                <div style={{ marginTop: 8 }}>
                  <div className="muted">Uploading selected files...</div>
                  <div style={{ height: 8, borderRadius: 999, background: "var(--color-border)" }}>
                    <div
                      style={{
                        width: `${
                          Object.values(uploadProgressByFile).length
                            ? Math.round(
                                Object.values(uploadProgressByFile).reduce((sum, n) => sum + Number(n || 0), 0) /
                                  Object.values(uploadProgressByFile).length
                              )
                            : 0
                        }%`,
                        height: "100%",
                        background: "var(--color-electric)",
                        borderRadius: 999,
                      }}
                    />
                  </div>
                </div>
              ) : null}
            </div>
            <div style={{ marginTop: 12 }}>
              <DataTable
                columns={columns}
                rows={documents}
                empty={
                  <EmptyState
                    title="No documents uploaded yet."
                    description="Drag a PDF above to add one."
                  />
                }
              />
            </div>
          </Card>
        </div>
      )}
      {previewUrl ? (
        <Modal title="PDF Preview" onClose={() => setPreviewUrl("")}>
          <iframe title="pdf-preview" src={previewUrl} style={{ width: "100%", height: "min(70vh, 560px)", border: "none" }} />
        </Modal>
      ) : null}
      {deleteDocTarget ? (
        <Modal
          title="Delete document?"
          danger
          confirmLabel="Delete"
          onClose={() => setDeleteDocTarget(null)}
          onConfirm={async () => {
            try {
              await deleteDocument(id, deleteDocTarget, actor);
              push("Document deleted", "success");
              setDeleteDocTarget(null);
              load();
            } catch (err) {
              push(getDocActionErrorMessage(err, "Unable to delete this document right now."), "error", {
                actionLabel: "Retry",
                onAction: () => setDeleteDocTarget((prev) => prev || deleteDocTarget),
                duration: 6000,
              });
            }
          }}
        >
          <p>This cannot be undone.</p>
        </Modal>
      ) : null}
    </AppShell>
  );
}
